import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  NotificationContainer,
  NotificationManager,
} from 'react-notifications';
import { BeatLoader } from 'react-spinners';
import { useModal } from '../context/ModalContext';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Text from '../components/common/Text';
import SectionHeader from '../components/common/SectionHeader';
import HotelLogo from '../components/common/HotelLogo';
import BillTable from '../components/bill/BillTable';
import {
  createNewFolio,
  getFoliosOfReservation,
  getReservation,
  sendEvent,
  setCurrentFolioIds,
} from '../redux/reducer/actions';
import { clearSelectedItems } from '../redux/reducer/slice';

const YourBillPage = () => {
  const { openModal, closeModal } = useModal();
  const [isPayingPerItem, setIsPayingPerItem] = useState(false);
  const [isFullyPaid, setIsFullyPaid] = useState(false);
  const reservation = useSelector((state) => state.app.reservation).data;
  const reservationFolios = useSelector((state) => state.app.reservationFolios);
  const selectedItems = useSelector((state) => state.app.selectedItems);
  const { billingAddressPageUrl, invoicesPageUrl } = useSelector((state) => state.app.urls);
  const [billData, setBillData] = useState([]);
  const dispatch = useDispatch();
  const { uniqueId, propertyId, selectedItemId } = useParams();
  const navigate = useNavigate();
  const [mainFolio, setMainFolio] = useState();
  useEffect(() => {
    dispatch(
      getReservation({ uniqueId, hotelId: propertyId, roomId: selectedItemId }),
    );
    dispatch(clearSelectedItems());
  }, [dispatch, propertyId, selectedItemId, uniqueId]);

  useEffect(() => {
    if (reservation) {
      dispatch(
        getFoliosOfReservation({
          uniqueId,
          hotelId: propertyId,
          reservationId: reservation.id,
        }),
      );
      if (reservation.balance.amount === 0) {
        setIsFullyPaid(true);
      }
    }
  }, [dispatch, propertyId, reservation, uniqueId]);

  const getMovableCharges = (folios) => {
    const chargeOccurrences = new Map(); // Track charge occurrences across folios

    const validFolios = folios.filter(
      (folio) => folio.status === 'Open' && folio.balance.amount !== 0,
    );

    validFolios.forEach((folio) => {
      const allCharges = [
        ...(folio.charges || []),
        ...(folio.transitoryCharges || []),
      ];

      allCharges.forEach((charge) => {
        const chargeId = charge.id;
        if (charge.movedTo) return;

        if (!chargeOccurrences.has(chargeId)) {
          chargeOccurrences.set(chargeId, { count: 1, charge });
        } else {
          chargeOccurrences.get(chargeId).count = chargeOccurrences.get(chargeId).count + 1;
        }
      });
    });

    const uniqueCharges = [];
    chargeOccurrences.forEach(({ charge }) => {
      uniqueCharges.push({
        ...charge,
        isMovable: !charge.movedTo && !charge.movedFrom,
      });
    });

    return uniqueCharges;
  };

  useEffect(() => {
    if (reservationFolios && reservationFolios.length > 0) {
      const charges = getMovableCharges(reservationFolios);
      const mainFolio = reservationFolios.filter(
        (folio) => folio.isMainFolio === true,
      )[0];
      setBillData(charges);
      setMainFolio(mainFolio);
    }
  }, [mainFolio, reservationFolios]);

  const splittingBillSpinnerModal = {
    title: 'Splitting your bill',
    body: (
      <div>
        <div>
          Please wait while we split your bill. This may take a
          few moments. Do not refresh this page.
        </div>
        <div className="mt-8 flex justify-center">
          <BeatLoader
            color="var(--color-pj-primary-300)"
            loading
            size={16}
            speedMultiplier={0.6}
          />
        </div>
      </div>

    ),
  };

  const handleBillSplit = async () => {
    closeModal();
    openModal(splittingBillSpinnerModal);

    if (selectedItems && selectedItems.length > 0) {
      const response = await dispatch(
        createNewFolio({
          hotelId: uniqueId,
          reservationId: reservation.id,
          folioId: mainFolio.id,
          debitor: mainFolio.debitor,
          chargeIds: selectedItems,
          type: mainFolio.type,
        }),
      );
      if (response && response.type && response.type.includes('rejected')) {
        NotificationManager.error(
          'Splitting the items failed! Please check with Reception',
          'Notification',
          3000,
        );
        dispatch(
          sendEvent({
            roomId: reservation.property.code,
            reservationId: reservation.id,
            type: 'PAY_BY_ITEM_FAILED',
          }),
        );
      } else {
        const newFolioId = response.payload;
        dispatch(setCurrentFolioIds({ folioIds: [newFolioId] }));
        dispatch(
          sendEvent({
            roomId: reservation.property.code,
            reservationId: reservation.id,
            type: 'PAY_BY_ITEM_SUCCESS',
          }),
        );
        await dispatch(
          getFoliosOfReservation({
            uniqueId,
            hotelId: uniqueId,
            reservationId: reservation.id,
          }),
        );
        navigate(billingAddressPageUrl);
      }
    }
    closeModal();
  };

  const billSplitConfirmationModal = {
    title: 'Split the bill?',
    body: (
      <>
        <div className="flex flex-col gap-3">
          <Text>
            Your bill is now being split for the selected items. Once the
            process is complete, you will have the option to update the invoice
            address.
          </Text>
          <Text>
            <span className="font-bold">Important: </span>
            <span>This action cannot be undone. Do you wish to proceed?</span>
          </Text>
        </div>
        <div className="mt-4">
          <h3 className="py-2 text-xl font-bold">Items</h3>
          <ul className="flex flex-col gap-3">
            {selectedItems.map((id) => {
              const itemDetails = billData?.find((item) => item.id === id);
              if (!itemDetails) { return null; }
              return (
                <li
                  key={id}
                  className="flex w-full justify-between gap-4 rounded-lg bg-pj-gray-500 p-2"
                >
                  <span>{itemDetails?.name}</span>
                  <span>{itemDetails?.amount?.grossAmount}</span>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="mb-6" />
      </>
    ),
    primaryActionLabel: 'Continue',
    primaryActionCallback: handleBillSplit,
    secondaryActionLabel: 'Go back',
    secondaryActionCallback: closeModal,
  };

  const payEntireBillButton = (
    <Button
      variant="primary"
      text="Pay entire bill"
      type="button"
      onClick={() => {
        const folios = reservationFolios.map((folio) => folio.id);
        dispatch(
          sendEvent({
            roomId: reservation.property.code,
            reservationId: reservation.id,
            type: 'PAY_ENTIRE_BILL',
          }),
        );
        dispatch(setCurrentFolioIds({ folioIds: folios }));
        navigate(billingAddressPageUrl);
      }}
    />
  );

  const payPerItemButton = (
    <Button
      variant="secondary"
      text="Pay per item"
      type="button"
      onClick={() => setIsPayingPerItem(true)}
    />
  );

  const goBackButton = (
    <Button
      variant="dark"
      text="Go back"
      type="button"
      onClick={() => setIsPayingPerItem(false)}
    />
  );

  const continueButton = (
    <Button
      variant="primary"
      text="Continue"
      disabled={selectedItems && selectedItems.length === 0}
      type="button"
      onClick={() => openModal(billSplitConfirmationModal)}
    />
  );

  return (
    <main className="flex min-h-screen flex-col gap-16 py-16">
      <HotelLogo />
      <section id="bill-breakdown" className="flex flex-col gap-6">
        <SectionHeader text="Your Bill" />
        {isFullyPaid && (
        <>
          <Text>All items are paid.</Text>
          <Button
            variant="primary"
            text="Checkout"
            type="button"
            onClick={() => navigate(invoicesPageUrl)}
          />
        </>
        )}

        {isPayingPerItem && !isFullyPaid && (
          <Text>
            Kindly be advised that certain items cannot be moved without prior
            authorization from the hotel. If this impacts your invoice, please
            contact the reception for assistance.
          </Text>
        )}
        {!isFullyPaid && (
        <>
          <div className="mt-2" />
          {billData.length === 0 ? (
            <LoadingSpinner message="Fetching bill items" />
          ) : (
            reservation && (
            <>
              <BillTable billData={billData} isPayingPerItem={isPayingPerItem} />

              <div className="my-2 flex w-full flex-col gap-4 xxs:flex-row">
                {isPayingPerItem ? (
                  <>
                    {goBackButton}
                    {continueButton}
                  </>
                ) : (
                  <>
                    {payEntireBillButton}
                    {billData && billData.length > 1 && payPerItemButton}
                  </>
                )}
              </div>
            </>
            )
          )}
        </>
        )}
        <NotificationContainer />
      </section>
    </main>
  );
};

export default YourBillPage;
